import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

let openModalCount = 0
let hiddenAppRoot = null
let rootWasInert = false
let rootAriaHidden = null

export function Modal({ open, onClose, title, eyebrow, children, dismissible = true, className = '' }) {
  const panelRef = useRef(null)
  const closeRef = useRef(onClose)
  const titleId = useId()

  useEffect(() => { closeRef.current = onClose }, [onClose])

  useEffect(() => {
    if (!open) return undefined
    const previous = document.activeElement
    const modalToken = `sukoji-modal-${titleId}-${Date.now()}`
    let ownsHistoryEntry = false
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && dismissible) closeRef.current?.()
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusable = panelRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    const onPopState = () => {
      if (!ownsHistoryEntry) return
      ownsHistoryEntry = false
      closeRef.current?.()
    }

    if (dismissible) {
      window.history.pushState(
        { ...(window.history.state || {}), sukojiModal: modalToken },
        '',
        window.location.href,
      )
      ownsHistoryEntry = true
      window.addEventListener('popstate', onPopState)
    }

    openModalCount += 1
    if (openModalCount === 1) {
      hiddenAppRoot = document.querySelector('.app-shell')
      rootWasInert = Boolean(hiddenAppRoot?.inert)
      rootAriaHidden = hiddenAppRoot?.getAttribute('aria-hidden') ?? null
      if (hiddenAppRoot) {
        hiddenAppRoot.inert = true
        hiddenAppRoot.setAttribute('aria-hidden', 'true')
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.body.classList.add('modal-open')
    requestAnimationFrame(() => panelRef.current?.querySelector('input, button')?.focus())
    return () => {
      window.removeEventListener('popstate', onPopState)
      document.removeEventListener('keydown', onKeyDown)
      if (ownsHistoryEntry && window.history.state?.sukojiModal === modalToken) {
        ownsHistoryEntry = false
        window.history.back()
      }
      openModalCount = Math.max(0, openModalCount - 1)
      if (openModalCount === 0) {
        document.body.classList.remove('modal-open')
        if (hiddenAppRoot) {
          hiddenAppRoot.inert = rootWasInert
          if (rootAriaHidden === null) hiddenAppRoot.removeAttribute('aria-hidden')
          else hiddenAppRoot.setAttribute('aria-hidden', rootAriaHidden)
        }
        hiddenAppRoot = null
        rootAriaHidden = null
      }
      previous?.focus?.()
    }
  }, [open, dismissible, titleId])

  if (!open) return null
  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={dismissible ? onClose : undefined}>
      <section
        ref={panelRef}
        className={`modal-panel ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {dismissible && (
          <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="Fermer">
            <X size={20} />
          </button>
        )}
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h2 id={titleId}>{title}</h2>
        {children}
      </section>
    </div>,
    document.body,
  )
}
